import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BugStatus =
  | 'reported' | 'acknowledged' | 'in_progress' | 'testing'
  | 'fixed' | 'verified' | 'closed' | 'wont_fix' | 'duplicate';

export type BugSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface Bug {
  _id: string;
  orgId?: string;
  title: string;
  description: string;
  impact: {
    severity: BugSeverity;
    affectedUsers?: number;
    affectedFeatures: string[];
  };
  reproductionSteps?: {
    steps: string[];
    environment?: string;
    browserVersion?: string;
    deviceInfo?: string;
  };
  applicationIds: string[];
  owningProjectId?: string;
  reportedInProjectId?: string;
  subprojectId?: string;
  sprintId?: string;
  reportedBy: {
    userId: string;
    name: string;
    email: string;
  };
  assignedTo: {
    userId: string;
    name: string;
    assignedAt?: string;
  }[];
  status: BugStatus;
  priority: BugSeverity;
  requiresAction: boolean;
  linkedTasks: string[];
  fixedIn?: {
    version?: string;
    deployedAt?: string;
    fixDescription?: string;
  };
  visibility: string;
  duplicateOf?: string;
  relatedBugs: string[];
  tags: string[];
  attachments: any[];
  closedBy?: string;
  closedByName?: string;
  reopenCount: number;
  reopenHistory: {
    reopenedBy: string;
    reopenedByName: string;
    reopenedAt: string;
    reason: string;
    previousClosedBy: string;
    previousClosedByName: string;
    previousClosedAt: string;
  }[];
  breachHistory: {
    originalDeadline: string;
    newDeadline: string;
    reason: string;
    measuresTaken: string;
    actionTaken: string;
    recordedBy: string;
    recordedByName: string;
    breachedAt: string;
  }[];
  reportedAt: string;
  acknowledgedAt?: string;
  resolvedAt?: string;
  verifiedAt?: string;
  closedAt?: string;
  commentsCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface BugComment {
  _id: string;
  bugId: string;
  content: string;
  contentType: 'text' | 'markdown';
  author: { userId: string; name: string; email: string };
  isInternal: boolean;
  edited: boolean;
  editedAt?: string;
  reactions: { emoji: string; userId: string; userName: string }[];
  parentCommentId?: string;
  isReply: boolean;
  replyCount: number;
  createdAt: string;
  updatedAt: string;
}

interface BugState {
  bugs: Bug[];
  currentBug: Bug | null;
  comments: BugComment[];
  total: number;
  page: number;
  loading: boolean;
  commentsLoading: boolean;
  error: string | null;
  filters: {
    status?: string;
    priority?: string;
    projectId?: string;
    sprintId?: string;
    assignedTo?: string;
  };
}

const initialState: BugState = {
  bugs: [],
  currentBug: null,
  comments: [],
  total: 0,
  page: 1,
  loading: false,
  commentsLoading: false,
  error: null,
  filters: {},
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchBugs = createAsyncThunk(
  'bugs/fetchAll',
  async (params: Record<string, string | undefined> = {}, { rejectWithValue }) => {
    try {
      const res = await api.get('/bugs', { params });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch bugs');
    }
  }
);

export const fetchBugById = createAsyncThunk(
  'bugs/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/bugs/${id}`);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch bug');
    }
  }
);

export const reportBug = createAsyncThunk(
  'bugs/report',
  async (data: Partial<Bug>, { rejectWithValue }) => {
    try {
      const res = await api.post('/bugs', data);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to report bug');
    }
  }
);

export const updateBug = createAsyncThunk(
  'bugs/update',
  async ({ id, data }: { id: string; data: Partial<Bug> }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/bugs/${id}`, data);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update bug');
    }
  }
);

export const acknowledgeBug = createAsyncThunk(
  'bugs/acknowledge',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.post(`/bugs/${id}/acknowledge`);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to acknowledge bug');
    }
  }
);

export const transitionBugStatus = createAsyncThunk(
  'bugs/transition',
  async ({ id, status }: { id: string; status: BugStatus }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/bugs/${id}/status`, { status });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update status');
    }
  }
);

export const assignBug = createAsyncThunk(
  'bugs/assign',
  async ({ id, userId, name }: { id: string; userId: string; name: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/bugs/${id}/assign`, { userId, name });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to assign bug');
    }
  }
);

export const resolveBug = createAsyncThunk(
  'bugs/resolve',
  async ({ id, version, fixDescription }: { id: string; version?: string; fixDescription?: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/bugs/${id}/resolve`, { version, fixDescription });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to resolve bug');
    }
  }
);

export const reopenBug = createAsyncThunk(
  'bugs/reopen',
  async ({ id, reason }: { id: string; reason: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/bugs/${id}/reopen`, { reason });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to reopen bug');
    }
  }
);

export const closeBug = createAsyncThunk(
  'bugs/close',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.post(`/bugs/${id}/close`);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to close bug');
    }
  }
);

export const assignBugToSprint = createAsyncThunk(
  'bugs/assignToSprint',
  async ({ id, sprintId }: { id: string; sprintId: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/bugs/${id}/sprint`, { sprintId });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to assign to sprint');
    }
  }
);

export const fetchBugComments = createAsyncThunk(
  'bugs/fetchComments',
  async (bugId: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/bugs/${bugId}/comments`);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch comments');
    }
  }
);

export const addBugComment = createAsyncThunk(
  'bugs/addComment',
  async ({ bugId, content, isInternal }: { bugId: string; content: string; isInternal?: boolean }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/bugs/${bugId}/comments`, { content, isInternal });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add comment');
    }
  }
);

export const deleteBugComment = createAsyncThunk(
  'bugs/deleteComment',
  async ({ bugId, commentId }: { bugId: string; commentId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/bugs/${bugId}/comments/${commentId}`);
      return commentId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete comment');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const updateBugInState = (state: BugState, updated: Bug) => {
  const idx = state.bugs.findIndex(b => b._id === updated._id);
  if (idx !== -1) state.bugs[idx] = updated;
  if (state.currentBug?._id === updated._id) state.currentBug = updated;
};

const bugSlice = createSlice({
  name: 'bugs',
  initialState,
  reducers: {
    clearBugError: (state) => { state.error = null; },
    clearCurrentBug: (state) => { state.currentBug = null; state.comments = []; },
    setFilters: (state, action) => { state.filters = action.payload; },
  },
  extraReducers: (builder) => {
    const pending = (state: BugState) => { state.loading = true; state.error = null; };
    const rejected = (state: BugState, action: any) => {
      state.loading = false;
      state.error = action.payload as string;
    };

    builder
      .addCase(fetchBugs.pending, pending)
      .addCase(fetchBugs.fulfilled, (state, action) => {
        state.loading = false;
        state.bugs = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page;
      })
      .addCase(fetchBugs.rejected, rejected)

      .addCase(fetchBugById.pending, pending)
      .addCase(fetchBugById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBug = action.payload;
      })
      .addCase(fetchBugById.rejected, rejected)

      .addCase(reportBug.pending, pending)
      .addCase(reportBug.fulfilled, (state, action) => {
        state.loading = false;
        state.bugs.unshift(action.payload);
      })
      .addCase(reportBug.rejected, rejected)

      .addCase(updateBug.fulfilled, (state, action) => { updateBugInState(state, action.payload); })
      .addCase(acknowledgeBug.fulfilled, (state, action) => { updateBugInState(state, action.payload); })
      .addCase(transitionBugStatus.fulfilled, (state, action) => { updateBugInState(state, action.payload); })
      .addCase(assignBug.fulfilled, (state, action) => { updateBugInState(state, action.payload); })
      .addCase(resolveBug.fulfilled, (state, action) => { updateBugInState(state, action.payload); })
      .addCase(reopenBug.fulfilled, (state, action) => { updateBugInState(state, action.payload); })
      .addCase(closeBug.fulfilled, (state, action) => { updateBugInState(state, action.payload); })
      .addCase(assignBugToSprint.fulfilled, (state, action) => { updateBugInState(state, action.payload); })

      .addCase(fetchBugComments.pending, (state) => { state.commentsLoading = true; })
      .addCase(fetchBugComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments = action.payload;
      })
      .addCase(fetchBugComments.rejected, (state) => { state.commentsLoading = false; })

      .addCase(addBugComment.fulfilled, (state, action) => {
        state.comments.push(action.payload);
      })
      .addCase(deleteBugComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter(c => c._id !== action.payload);
      });
  },
});

export const { clearBugError, clearCurrentBug, setFilters } = bugSlice.actions;
export default bugSlice.reducer;
